import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ShareIcon from "@material-ui/icons/Share";
import SearchIcon from "@material-ui/icons/Search";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { useDialogStyles } from "../../theme/redesign";
import { publicFileUrl } from "./helpers";
import { stopRow, tNegocio } from "./shared";

const emailSrcDoc = (html) => {
  const body = String(html || "");
  if (/<html/i.test(body)) return body;
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:12px;background:#fff;font-family:Arial,Helvetica,sans-serif;color:#111}</style></head><body>${body}</body></html>`;
};

const platformTheme = (name, tipo) => {
  const n = `${name || ""} ${tipo || ""}`.toLowerCase();
  if (/(netflix|hogar|viaje)/.test(n)) {
    return { brand: "#E50914", soft: "#fff1f2", ink: "#0f172a" };
  }
  if (/(disney|star\+)/.test(n)) {
    return { brand: "#113CCF", soft: "#eef2ff", ink: "#0f172a" };
  }
  if (/(hbo|max\b)/.test(n)) {
    return { brand: "#002BE7", soft: "#eef2ff", ink: "#0f172a" };
  }
  if (/(prime|amazon)/.test(n)) {
    return { brand: "#00A8E1", soft: "#e0f7ff", ink: "#0f172a" };
  }
  if (/paramount/.test(n)) {
    return { brand: "#0064FF", soft: "#eef4ff", ink: "#0f172a" };
  }
  if (/crunchyroll/.test(n)) {
    return { brand: "#F47521", soft: "#fff4ed", ink: "#0f172a" };
  }
  if (/spotify/.test(n)) {
    return { brand: "#1DB954", soft: "#ecfdf3", ink: "#0f172a" };
  }
  if (/apple/.test(n)) {
    return { brand: "#1d1d1f", soft: "#f4f4f5", ink: "#0f172a" };
  }
  if (/(youtube|directv)/.test(n)) {
    return { brand: "#FF0000", soft: "#fff1f1", ink: "#0f172a" };
  }
  return { brand: "#2563eb", soft: "#edf2ff", ink: "#0f172a" };
};

export function CodeLookupResultBody({ result, loading, onReset, publicMode = false }) {
  const tx = (key, es) => (publicMode ? es : tNegocio(key));
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
        <CircularProgress size={28} />
      </div>
    );
  }
  if (!result) return null;

  const links = result.links || [];
  const code = links.length ? "" : (result.codes || [])[0] || "";
  const found = !!(code || links.length);
  const productName = result.productName || "";
  const snippet = result.snippet || "";
  const html = result.html || "";
  const theme = platformTheme(productName, links[0]?.tipo);
  const statusLabel = links.length
    ? tx("pages.codes.linkFound", "Link encontrado")
    : tx("pages.codes.found", "Código encontrado");

  const copy = (value, asLink = false) => {
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success(asLink ? tx("pages.codes.copiedLink", "Enlace copiado") : tx("pages.codes.copied", "Código copiado")));
  };
  const share = (value) => {
    const text = productName ? `${productName}: ${value}` : value;
    if (navigator.share) {
      navigator.share({ title: productName || tx("pages.codes.lookupTitle", "Resultado de la consulta"), text, url: /^https?:/i.test(value) ? value : undefined }).catch(() => copy(value, /^https?:/i.test(value)));
      return;
    }
    copy(value, /^https?:/i.test(value));
  };

  if (!found) {
    return (
      <div>
        {onReset ? (
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={onReset}
            style={{ marginBottom: 16, textTransform: "none", fontWeight: 800, borderRadius: 999 }}
          >
            {tx("pages.codes.newSearch", "Nueva búsqueda")}
          </Button>
        ) : null}
        <div style={{ color: "#64748b", marginBottom: 12 }}>{result.message || tx("pages.codes.empty", "No hay un código reciente para este correo.")}</div>
      </div>
    );
  }

  const cleanSnippet = snippet && !/https?:|www\.|CLO\/|mail\d\./i.test(snippet) ? snippet : "";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        {onReset ? (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<SearchIcon />}
            onClick={onReset}
            style={{ textTransform: "none", fontWeight: 800, borderRadius: 999, paddingLeft: 12, paddingRight: 12 }}
          >
            {tx("pages.codes.newSearch", "Nueva búsqueda")}
          </Button>
        ) : null}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "#ecfdf5",
            color: "#047857",
            border: "1px solid #a7f3d0",
            borderRadius: 999,
            padding: "4px 10px",
            fontWeight: 800,
            fontSize: 12,
            lineHeight: 1.2,
          }}
        >
          <CheckCircleIcon style={{ fontSize: 14 }} />
          {statusLabel}
        </span>
      </div>
      <div
        style={{
          background: theme.soft,
          color: theme.ink,
          borderRadius: 12,
          padding: "10px 12px",
          marginBottom: 8,
          border: `1px solid ${theme.brand}22`,
        }}
      >
        {productName ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: theme.brand,
              fontWeight: 800,
              fontSize: 11,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              marginBottom: code ? 6 : cleanSnippet ? 4 : 0,
            }}
          >
            {result.productImage ? (
              <img
                src={publicFileUrl(result.productImage)}
                alt=""
                style={{ width: 16, height: 16, borderRadius: 4, objectFit: "cover", background: "#fff" }}
              />
            ) : null}
            {productName}
          </div>
        ) : null}
        {code ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                flex: 1,
                fontSize: 28,
                fontWeight: 900,
                letterSpacing: 5,
                fontFamily: "Inter, sans-serif",
                lineHeight: 1.1,
                color: theme.ink,
              }}
            >
              {code}
            </div>
            <div style={{ display: "flex", gap: 2 }}>
              <Tooltip title={tx("pages.codes.copied", "Código copiado")}>
                <IconButton size="small" onClick={() => copy(code)} style={{ color: theme.brand, padding: 4 }}>
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={tx("pages.codes.share", "Compartir")}>
                <IconButton size="small" onClick={() => share(code)} style={{ color: theme.brand, padding: 4 }}>
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        ) : null}
        {cleanSnippet ? (
          <div
            style={{
              marginTop: code ? 6 : 2,
              color: "#64748b",
              fontSize: 12,
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {cleanSnippet}
          </div>
        ) : null}
      </div>
      {links.map((link) => {
        const buttonTheme = platformTheme(productName, link.tipo);
        return (
          <Button
            key={link.url}
            fullWidth
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginBottom: 8,
              textTransform: "none",
              background: buttonTheme.brand,
              color: "#fff",
              fontWeight: 800,
              borderRadius: 10,
              minHeight: 44,
              fontSize: 14,
            }}
          >
            {link.texto}
          </Button>
        );
      })}
      {html ? (
        <div
          style={{
            border: "1px solid rgba(15,23,42,0.10)",
            borderRadius: 12,
            overflow: "hidden",
            background: "#fff",
          }}
        >
          <iframe
            title={tx("pages.codes.preview", "Correo")}
            sandbox=""
            referrerPolicy="no-referrer"
            srcDoc={emailSrcDoc(html)}
            style={{ width: "100%", height: 280, border: 0, background: "#fff", display: "block" }}
          />
        </div>
      ) : null}
    </div>
  );
}

export function CodeLookupDialog({ open, onClose, productId, email, contactId, subscriptionId }) {
  const dlg = useDialogStyles();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!open) {
      setResult(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/negocio/codes/lookup", { productId, email, contactId, subscriptionId });
        if (!cancelled) setResult(data);
      } catch (error) {
        if (!cancelled) {
          setResult(null);
          toastError(error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, productId, email, contactId, subscriptionId, tick]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth classes={{ paper: dlg.paper }}>
      <DialogTitle>{tNegocio("pages.codes.lookupTitle")}</DialogTitle>
      <DialogContent dividers>
        <CodeLookupResultBody result={result} loading={loading} onReset={() => setTick((n) => n + 1)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tNegocio("close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function CodeLookupButton({ sub, customer }) {
  const [open, setOpen] = useState(false);
  const email = sub?.account?.email;
  const can = !!(sub?.codesEnabled && email && sub?.productId);
  if (!can) return null;
  return (
    <>
      <Tooltip title={tNegocio("pages.codes.lookup")}>
        <IconButton size="small" onClick={stopRow(() => setOpen(true))} style={{ color: "#1d4ed8" }}>
          <VpnKeyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <CodeLookupDialog
        open={open}
        onClose={() => setOpen(false)}
        productId={sub.productId}
        email={email}
        contactId={customer?.contactId}
        subscriptionId={sub.id}
      />
    </>
  );
}
